import { type Member } from '@/features/iam/members/Member'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import { action, computed, observable } from 'mobx'
import { EMPTY, type Observable, Subject, firstValueFrom } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  switchMap,
  takeUntil,
} from 'rxjs/operators'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { fromStream, toStream } from '@/utils/mobx-util'
import {
  fromAbortSignal,
  shareLatest,
  whileActive,
  withAbortSignal,
} from '@/utils/rx-util'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type SupportingClientSelected } from './DraftPersister'
import { type DraftStateSlice, type PrepareContext } from './DraftJobViewModel'
import { type StepFactory, type StepViewModel } from './OutlineViewModel'
import type { SearchMembersFn } from '@/features/iam'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'

const TITLE = 'Supporting clients'

/**
 * Supporting client Selector view model, for selecting the supporting client
 */
export class SupportingClientSelectorViewModel
  implements ActivationLifecycle, DraftStateSlice
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Whether `prepare` has completed since activation.
   */
  @observable private accessor _isReady = false

  private readonly _isReady$ = toStream(() => this._isReady, true)

  /**
   * The singleton step for this slice.
   */
  readonly step: StepViewModel

  /**
   * Changes subject.
   * @private
   */
  private readonly _changesSubject = new Subject<SupportingClientSelected>()

  /**
   * Stream for whether the step is valid.
   */
  readonly isValid$ = toStream(() => this.isValid, true)

  /**
   * The search text.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Changes stream.
   */
  readonly changes$ = this._changesSubject.asObservable()

  /**
   * The supporting clients selected.
   */
  @observable private accessor _selectedSupportingClient: Member[] = []

  /**
   * Emits the selected supporting clients on subscribe, but only after
   * `prepare` has been run.
   */
  readonly selectedSupportingClient$ = toStream(
    () => this.selectedSupportingClient,
    true,
  ).pipe(
    distinctUntilChanged(),
    shareLatest(),
    // Don't start emitting until the slice is ready.
    whileActive(this._isReady$),
  )

  /**
   * Streams as MobX Observables.
   */
  private readonly _$

  /**
   * The scroll provider for Members.
   */
  readonly scroller = new InfiniteScrollProvider<Member>({
    limit: 20,
    fetchItems: async ({ paging, signal }) =>
      this._createAlertRetryWrapper(
        'Loading clients failed',
        signal,
      )(() => this._listMembers(paging, signal)),
  })

  /**
   * Constructor.
   *
   * @param createStep Factory for creating a Step.
   * @param selectedWorkspaceMember$ Observable for the selected workspace member
   * @param selectedOwner$ Observable for the selected job owner
   * @param _isDisabled Getter for whether the selector is disabled
   * @param _jobWasCreated Getter for whether the selector is visible
   */
  constructor(
    createStep: StepFactory,
    private readonly _selectedWorkspaceMember$: Observable<Member | null>,
    private readonly _selectedOwner$: Observable<Member | null>,
    private readonly _searchMembers: SearchMembersFn,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _isDisabled: () => boolean,
    private readonly _jobWasCreated: () => boolean,
  ) {
    this._$ = {
      selectedWorkspaceMember: fromStream(null, this._selectedWorkspaceMember$),
      selectedOwner: fromStream(null, this._selectedOwner$),
    }

    this._lifecycle.manage(this.selectedSupportingClient$)

    this._lifecycle.manage(
      toStream(() => this.selectedSupportingClient, true).pipe(
        switchMap(() =>
          withAbortSignal((signal) => this.scroller.fetch({ signal })).pipe(
            catchError(() => EMPTY),
          ),
        ),
        whileActive(this._isReady$),
      ),
    )

    this._lifecycle.manage(
      this.searchText.debouncedValue$.pipe(
        switchMap(() =>
          withAbortSignal((signal) => this.scroller.fetch({ signal })).pipe(
            catchError(() => EMPTY),
          ),
        ),
        whileActive(this._isReady$),
      ),
    )

    this.step = createStep({
      stepId: 'client_support_selection',
      isCompleted: () => Boolean(this._selectedSupportingClient),
      outlineItemDisplay: () => {
        return { title: TITLE }
      },
    })
  }

  /**
   * Whether the step is valid.
   */
  @computed
  get isValid() {
    return Boolean(this.selectedSupportingClient)
  }

  /**
   * Whether the user may manage the job's supporting clients.
   */
  @computed
  get canManageSupportingClients() {
    const member = this._$.selectedWorkspaceMember()
    return member?.permissions.adminJobClientsManage ?? false
  }

  /**
   * This is the only step accessor.
   */
  @computed
  get stepAccessors() {
    if (!this._jobWasCreated() || !this.canManageSupportingClients) {
      return []
    }

    return [this]
  }

  /**
   * Whether the supporting client selector is disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /**
   * The currently selected supporting client
   */
  @computed
  get selectedSupportingClient() {
    return this._selectedSupportingClient
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  @action.bound
  deactivate() {
    this._isReady = false
    this._lifecycle.deactivate()
  }

  /** @inheritDoc */
  @action.bound
  ready() {
    this._isReady = this._lifecycle.active
  }

  /**
   * Hydrates the supporting client state from the Job.
   *
   * @param context
   * @param signal
   */
  async prepare(context: PrepareContext, signal?: AbortSignal) {
    // Wait for the WS selector to have been prepared.
    await firstValueFrom(
      this._selectedOwner$.pipe(takeUntil(fromAbortSignal(signal))),
      { defaultValue: null },
    )

    signal?.throwIfAborted()

    if (context.type === 'EditDraft') {
      this.setClientSupport(context.job.supportingClients.map((c) => c.member))
      return
    }

    this.setClientSupport([])
  }

  /**
   * Sets the selected supporting client.
   *
   * @param clientSupport
   */
  @action.bound
  setClientSupport(clientSupport: Member[]) {
    this._selectedSupportingClient = clientSupport
  }

  /**
   * Add a supporting client.
   *
   * @param clientSupport
   */
  @action.bound
  addClientSupport(clientSupport: Member) {
    this._changesSubject.next({
      member: clientSupport,
      type: 'SupportingClientAdded',
    })
  }

  /**
   * Remove a supporting client.
   *
   * @param clientSupport
   */
  @action.bound
  removeClientSupport(clientSupport: Member) {
    this._changesSubject.next({
      member: clientSupport,
      type: 'SupportingClientRemoved',
    })
  }

  /**
   * Selects the specified supporting clients.
   *
   * @param clientSupports
   */
  @action.bound
  selectClientSupport(clientSupports: Member[]) {
    const toAdd = clientSupports.filter(
      (c) =>
        !this._selectedSupportingClient.find(
          (s) => s.user.publicId === c.user.publicId,
        ),
    )
    const toRemove = this._selectedSupportingClient.filter(
      (s) => !clientSupports.find((c) => c.user.publicId === s.user.publicId),
    )

    toAdd.forEach(this.addClientSupport)
    toRemove.forEach(this.removeClientSupport)
    this.setClientSupport(clientSupports)
  }

  /**
   * Triggers new member search.
   *
   * @param newText
   */
  @action.bound
  triggerSearch(newText: string) {
    this.searchText.change(newText)
  }

  /**
   * Lists the workspace's members.
   *
   * @private
   */
  private async _listMembers(paging: CursorPageQuery, signal?: AbortSignal) {
    const member = this._$.selectedWorkspaceMember()
    if (!member) {
      return CursorPaged.empty<Member>()
    }

    if (
      !member.permissions.adminUserList &&
      !member.permissions.adminClientUserList
    ) {
      return CursorPaged.of([member])
    }

    let page = await this._searchMembers(
      {
        ...paging,
        workspaceId: member.workspace.id,
        searchText: this.searchText.value,
        userTypes:
          !member.permissions.adminUserList &&
          member.permissions.adminClientUserList
            ? ['User']
            : undefined,
      },
      signal,
    )

    const selectedOwner = this._$.selectedOwner()
    if (selectedOwner) {
      page = CursorPaged.map(page, (members) =>
        members.filter((m) => m.user.publicId !== selectedOwner.user.publicId),
      )
    }
    return page
  }
}

/**
 * Factory for creating a {@link SupportingClientSelectorViewModel}.
 */
export type SupportingClientSelectorViewModelFactory = ReturnType<
  typeof createSupportingClientSelectorViewModelFactory
>

/** @see SupportingClientSelectorViewModelFactory */
export function createSupportingClientSelectorViewModelFactory(
  searchMembers: SearchMembersFn,
  createAlertRetryWrapper: AlertRetryWrapperFactory,
) {
  return (
    createStep: StepFactory,
    selectedWorkspaceMember$: Observable<Member | null>,
    selectedOwner$: Observable<Member | null>,
    isDisabled: () => boolean,
    jobWasCreated: () => boolean,
  ) =>
    new SupportingClientSelectorViewModel(
      createStep,
      selectedWorkspaceMember$,
      selectedOwner$,
      searchMembers,
      createAlertRetryWrapper,
      isDisabled,
      jobWasCreated,
    )
}
